import type {
  AgentPage,
  AgentBlockListQuery,
  AgentInstanceListQuery,
} from '../../types/negotiationAgents';
import { apiDelete, apiGet, apiPost, apiPut } from '@/shared/utils/requestBuilder';
import { parseApiResponse } from '@/shared/utils/apiResponse';
import type {
  FlowCustomGroup,
  FlowCustomGroupInstance,
  FlowCustomGroupInstanceInput,
  FlowCustomGroupInput,
} from '../../types/flows';

const BASE_URL = '/admin/flow-custom-groups';

export const flowCustomGroupsService = {
  async listGroups(query: AgentBlockListQuery = {}): Promise<AgentPage<FlowCustomGroup>> {
    const params = new URLSearchParams(
      Object.entries({ page: 1, limit: 20, ...query })
        .filter(([, value]) => value !== undefined && value !== '')
        .map(([key, value]) => [key, String(value)]),
    );
    const response = await apiGet(`${BASE_URL}?${params}`);
    return parseApiResponse<AgentPage<FlowCustomGroup>>(response);
  },

  async getGroup(groupId: string): Promise<FlowCustomGroup> {
    const response = await apiGet(`${BASE_URL}/${groupId}`);
    return parseApiResponse<FlowCustomGroup>(response);
  },

  async createGroup(data: FlowCustomGroupInput): Promise<FlowCustomGroup> {
    const response = await apiPost(BASE_URL, data);
    return parseApiResponse<FlowCustomGroup>(response);
  },

  async updateGroup(groupId: string, data: FlowCustomGroupInput): Promise<FlowCustomGroup> {
    const response = await apiPut(`${BASE_URL}/${groupId}`, data);
    return parseApiResponse<FlowCustomGroup>(response);
  },

  async deleteGroup(groupId: string): Promise<void> {
    await apiDelete(`${BASE_URL}/${groupId}`);
  },

  async listInstances(query: AgentInstanceListQuery): Promise<AgentPage<FlowCustomGroupInstance>> {
    const response = await apiGet(
      `${BASE_URL}/flows/${query.flowId}/instances?page=${query.page ?? 1}&limit=${query.limit ?? 20}`,
    );
    return parseApiResponse<AgentPage<FlowCustomGroupInstance>>(response);
  },

  async getInstance(flowId: string, instanceId: string): Promise<FlowCustomGroupInstance> {
    const response = await apiGet(`${BASE_URL}/flows/${flowId}/instances/${instanceId}`);
    return parseApiResponse<FlowCustomGroupInstance>(response);
  },

  async createInstance(
    flowId: string,
    data: FlowCustomGroupInstanceInput,
  ): Promise<FlowCustomGroupInstance> {
    const response = await apiPost(`${BASE_URL}/flows/${flowId}/instances`, data);
    return parseApiResponse<FlowCustomGroupInstance>(response);
  },

  async updateInstance(
    flowId: string,
    instanceId: string,
    data: FlowCustomGroupInstanceInput,
  ): Promise<FlowCustomGroupInstance> {
    const response = await apiPut(`${BASE_URL}/flows/${flowId}/instances/${instanceId}`, data);
    return parseApiResponse<FlowCustomGroupInstance>(response);
  },

  async deleteInstance(flowId: string, instanceId: string): Promise<void> {
    await apiDelete(`${BASE_URL}/flows/${flowId}/instances/${instanceId}`);
  },
};
